<template>
	<section class="locations-overview">
		<div class="row locations-row">
			<div v-for="location in items" :key="location.ID" class="location-item columns column6">
				<div class="location-wrapper">
					<div class="image-holder">
						<nuxt-link :to="location.url">
							<div class="image">
								<picture>
									<source :srcset="location.imageWebp" type="image/webp" />
									<source :srcset="location.image" />
									<img :src="location.image" :alt="location.imageAlt" loading="lazy" />
								</picture>
							</div>
						</nuxt-link>
					</div>
					<div class="content">
						<h2>
							{{ location.header }}
						</h2>
						<nuxt-link :to="location.gmapsUrl" target="_blank" class="maps-link">
							<span class="location-marker" />
							<span>{{ location.address }}</span>
						</nuxt-link>
						<div v-parse-links v-html="location.description" />

						<div v-if="location.facilities.length" class="location-facilities">
							<h4>{{ $t('facilities') }}</h4>
							<div class="facilities-row">
								<div v-for="facility in location.facilities" :key="facility.ID" class="facility-item">
									<picture>
										<source :srcset="facility.iconWebp" type="image/webp" />
										<source :srcset="facility.icon" />
										<img
											:src="facility.icon"
											:alt="facility.iconAlt"
											class="facility-icon"
											loading="lazy"
										/>
									</picture>
									<span>{{ facility.header }}</span>
								</div>
							</div>
						</div>
						<div class="button-wrapper">
							<nuxt-link :to="location.url" class="button">
								{{ defaults[locale].hotels.buttonExploreThisHotel }}
							</nuxt-link>
							<book-button :hotel-id="location.bookingUrl" class="button button-cta">
								{{ defaults[locale].hotels.buttonBookNow }}
							</book-button>
						</div>
					</div>
				</div>
			</div>
		</div>
	</section>
</template>

<script setup>
const { defaults } = useDefaults();
const { locale } = useI18n();

defineProps({
	type: { type: String, default: 'hotels' },
	items: { type: Array, default: () => [] },
});
</script>

<style lang="scss" scoped>
.locations-overview {
	padding: 0;

	.content {
		.button {
			min-width: 210px;
			text-align: center;
			margin: 8px 10px 8px 0;
		}
	}
}

.locations-row {
	justify-content: center;
	max-width: 100%;

	.column6 {
		.location-wrapper {
			max-width: 750px;
		}
	}
}

.button-wrapper {
	align-items: flex-start;
	display: flex;
	flex-flow: row wrap;
	justify-content: flex-start;
}

.image-holder {
	a::after {
		display: none;
	}
}

.location-item {
	flex-flow: column wrap;
	position: relative;
	justify-content: flex-start;
	align-items: flex-start;
	background: #fff;

	&:first-child {
		h2 {
			color: #cfa74d;
		}
	}

	&:nth-child(2) {
		h2 {
			color: var(--header-color-quaternary);
		}
	}

	&:nth-child(3) {
		h2 {
			color: var(--header-color);
		}
	}

	&:nth-child(4) {
		h2 {
			color: #cddada;
		}
	}

	&:nth-child(5) {
		h2 {
			color: #bb979a;
		}
	}

	&:nth-child(6) {
		h2 {
			color: #9cb0a9;
		}
	}

	&:nth-child(7) {
		h2 {
			color: var(--cta-color-blue);
		}
	}

	&:nth-child(8) {
		h2 {
			color: #8b6d8b;
		}
	}

	&:nth-child(9) {
		h2 {
			color: #17243d;
		}
	}

	h2 {
		margin: 60px 0 30px;
		min-height: 110px;
	}

	h4 {
		color: var(--body-color);
		margin: 0;
	}

	.location-wrapper {
		padding: 0 100px;
		margin: 60px auto 80px;
	}

	&:nth-child(odd) {
		.location-wrapper {
			&::after {
				content: '';
				display: block;
				width: 1px;
				background: #3c3834;
				position: absolute;
				right: 0;
				top: 0;
				bottom: 75px;
			}
		}
	}

	&:last-child {
		.location-wrapper {
			&::after {
				display: none;
			}
		}
	}

	&:nth-child(1),
	&:nth-child(2),
	&:nth-child(5),
	&:nth-child(6),
	&:nth-child(9),
	&:nth-child(10),
	&:nth-child(13),
	&:nth-child(14) {
		background: var(--primary-background-color);
	}

	.maps-link {
		cursor: pointer;
		display: flex;
		flex-flow: row nowrap;
		justify-content: flex-start;
		align-items: center;
		text-decoration: none;
		color: var(--body-color);

		.location-marker {
			background: url('~/assets/images/location-marker-black.png') no-repeat center center;
			width: 36px;
			height: 40px;
			background-size: 40px;
			display: block;
			margin: 0 5px 0 0;
		}
	}

	.image {
		width: 100%;

		img {
			clip-path: polygon(50% 13%, 100% 30%, 100% 100%, 0% 100%, 0% 30%);
			overflow: hidden;
			aspect-ratio: 2 / 3;
		}
	}
}

.location-facilities {
	margin: 40px auto;
}

.facilities-row {
	display: grid;
	grid-template-columns: 1fr 1fr;
	align-items: center;
	gap: 15px 5px;
	margin: 20px 0 0;

	.facility-item {
		display: flex;
		flex-flow: row nowrap;
		justify-content: flex-start;
		align-items: center;
		gap: 5px;

		span {
			max-width: calc(100% - 50px);
		}
	}

	.facility-icon {
		width: 40px;
		height: 40px;
		margin: 0 5px 0 0;
	}
}

@supports (-webkit-mask-image: url('#mask')) or (mask-image: url('#mask')) {
	.location-item {
		.image {
			img {
				clip-path: none;
				mask-image: url('~/assets/images/locations-shape-mask.png');
				mask-size: 100%;
			}
		}
	}
}

@media (max-width: 1080px) {
	.facilities-row {
		grid-template-columns: 1fr;
		gap: 0;
	}

	.locations-overview {
		.location-item,
		.location-item:nth-child(odd) {
			padding: 0 20px;

			.location-wrapper {
				padding: 0 50px;

				&::after {
					display: none;
				}
			}
		}

		.location-item {
			background: #fff;

			&:nth-child(1),
			&:nth-child(3),
			&:nth-child(5),
			&:nth-child(7),
			&:nth-child(9),
			&:nth-child(11),
			&:nth-child(13),
			&:nth-child(15) {
				background: var(--primary-background-color);
			}
		}
	}
}

@media (max-width: 800px) {
	.locations-overview {
		.location-item,
		.location-item:nth-child(odd) {
			h2 {
				margin: 40px 0 15px;
			}

			.location-wrapper {
				padding: 0 20px;
			}
		}
	}
}
</style>
